import { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AgentChatMessageFragment } from '@treams/treams-network';
import { Avatar, FlexGap, Heading, space, tokens } from '@treams/treams-ui';

import { AgentChatComposer } from './AgentChatComposer';
import { AgentChatEmpty } from './AgentChatEmpty';
import { AgentChatMessageList } from './AgentChatMessageList';

const EMPTY_WIDTH = 640;
const THREAD_WIDTH = 720;

const ThreadContainer = styled(Flex)<{ $compact?: boolean }>`
	flex: 1;
	min-height: 0;
	height: 100%;
	flex-direction: column;
	background: ${tokens.surface.white};
	overflow: ${(props) => (props.$compact ? 'hidden' : 'visible')};
`;

const Column = styled(Box)<{ $maxWidth: number }>`
	width: 100%;
	max-width: ${(props) => props.$maxWidth}px;
	margin-inline: auto;
`;

const EmptyPane = styled(Flex)<{ $compact?: boolean }>`
	flex: 1;
	min-height: 0;
	flex-direction: column;
	overflow-y: auto;
	overscroll-behavior: contain;
	padding: ${(props) =>
		props.$compact
			? `${tokens.padding.container}px`
			: `${space['3xl']}px ${tokens.padding.page}px`};
`;

const ThreadInner = styled(Flex)`
	flex: 1;
	min-height: 0;
	flex-direction: column;
`;

const ComposerBar = styled(Box)<{ $compact?: boolean }>`
	position: sticky;
	bottom: 0;
	z-index: 1;
	flex-shrink: 0;
	background: ${tokens.surface.white};
	padding: ${(props) =>
		props.$compact
			? `${tokens.gap.group}px ${tokens.padding.container}px ${tokens.padding.container}px`
			: `${tokens.gap.group}px 0 ${tokens.padding.page}px`};
`;

const SubjectHeader = styled(FlexGap)`
	flex-direction: column;
	align-items: center;
	padding: ${tokens.padding.page}px ${tokens.padding.page}px 0;
`;

export type AgentChatSubject = {
	id: string;
	firstName: string;
	lastName: string;
	avatar?: { source: { url: string } } | null;
} | null;

export function AgentChatThread(props: {
	messages: AgentChatMessageFragment[];
	isLoading?: boolean;
	isStreaming: boolean;
	error?: string;
	draft: string;
	onDraftChange: (value: string) => void;
	onSubmit: (event: FormEvent) => void;
	onSelectSuggestion: (message: string, subjectID?: string) => void;
	onStartOver: () => void;
	showComposer: boolean;
	estimatedCost?: number;
	subject?: AgentChatSubject;
	compact?: boolean;
}) {
	const { t } = useTranslation();
	const isEmpty = props.messages.length === 0 && !props.isStreaming && !props.isLoading;
	const subjectName = props.subject
		? `${props.subject.firstName} ${props.subject.lastName}`.trim()
		: undefined;

	const composer = props.showComposer ? (
		<AgentChatComposer
			draft={props.draft}
			disabled={props.isStreaming}
			placeholder={t('aiAgent.askPlaceholder')}
			onChange={props.onDraftChange}
			onSubmit={props.onSubmit}
		/>
	) : undefined;

	return (
		<ThreadContainer $compact={props.compact}>
			{isEmpty ? (
				<EmptyPane $compact={props.compact}>
					<Column $maxWidth={props.compact ? THREAD_WIDTH : EMPTY_WIDTH}>
						<AgentChatEmpty
							compact={props.compact}
							composer={composer}
							initialSubjectID={props.subject?.id}
							onSelectSuggestion={props.onSelectSuggestion}
						/>
					</Column>
				</EmptyPane>
			) : (
				<ThreadInner>
					{!props.compact && props.subject && subjectName && (
						<SubjectHeader gap={tokens.gap.group}>
							<Avatar size={48} user={props.subject} />
							<Heading>
								<Trans
									i18nKey="aiAgent.forSubject"
									components={{ strong: <strong /> }}
									values={{ name: subjectName }}
								/>
							</Heading>
						</SubjectHeader>
					)}
					<AgentChatMessageList
						messages={props.messages}
						isLoading={props.isLoading}
						isStreaming={props.isStreaming}
						error={props.error}
						compact={props.compact}
						onSelectSuggestion={props.onSelectSuggestion}
						onStartOver={props.onStartOver}
						estimatedCost={props.estimatedCost}
					/>
					{composer && (
						<ComposerBar $compact={props.compact}>
							<Column $maxWidth={THREAD_WIDTH}>{composer}</Column>
						</ComposerBar>
					)}
				</ThreadInner>
			)}
		</ThreadContainer>
	);
}
